import { isNil } from '@core/utils/shared-utils';

export class MapSource {
  private pendingTimeout?: NodeJS.Timeout;
  private lastDataHash: string | null = null;
  private isUpdating = false;
  private readonly maxRetries = 5;
  private retryCount = 0;

  constructor(
    readonly name: string,
    private readonly map: mapboxgl.Map
  ) {}

  updateSourceOnReady(data: GeoJSON.GeoJSON | string): void {
    const dataHash = this.hashData(data);
    if (this.lastDataHash === dataHash || this.isUpdating) {
      return;
    }

    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = undefined;
    }

    this.isUpdating = true;

    if (this.map.isSourceLoaded(this.name) || isNil(this.map.getSource(this.name))) {
      this.performUpdate(data, dataHash);
    } else if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.pendingTimeout = setTimeout(
        () => {
          this.pendingTimeout = undefined;
          this.isUpdating = false;
          this.updateSourceOnReady(data);
        },
        Math.min(100 * this.retryCount, 1000)
      );
    } else {
      console.warn(`Max retries reached for source ${this.name}`);
      this.isUpdating = false;
      this.retryCount = 0;
    }
  }

  private performUpdate(data: GeoJSON.GeoJSON | string, dataHash: string): void {
    try {
      const source = this.map.getSource(this.name) as mapboxgl.GeoJSONSource;
      if (source && !this.map._removed) {
        requestAnimationFrame(() => {
          try {
            source.setData(data);
            this.lastDataHash = dataHash;
            this.retryCount = 0;
          } catch (error) {
            console.warn(`Failed to update source ${this.name}:`, error);
          } finally {
            this.isUpdating = false;
          }
        });
      } else {
        this.isUpdating = false;
      }
    } catch (error) {
      console.warn(`Error accessing source ${this.name}:`, error);
      this.isUpdating = false;
    }
  }

  private hashData(data: GeoJSON.GeoJSON | string): string {
    if (typeof data === 'string') {
      return this.simpleHash(data);
    }

    if ('features' in data) {
      // Hash the actual feature content, not just count
      try {
        const content = JSON.stringify(data.features);
        return `fc_${this.simpleHash(content)}`;
      } catch {
        // Fallback to length-based if stringify fails
        return `fc_${data.features.length}_${Date.now()}`;
      }
    }

    return this.simpleHash(JSON.stringify(data));
  }

  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  cleanup(): void {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = undefined;
    }
    this.lastDataHash = null;
    this.isUpdating = false;
    this.retryCount = 0;
  }
}
